<template>
  <div
    v-if="loaded"
    class="settings-section"
    data-testid="reserve-settings-form"
  >
    <h4 class="section-title">{{ t('Reserva de admissão') }}</h4>
    <p class="section-subtitle">
      {{ t('Antes de abrir uma conversa o sistema segura um valor de pior caso. É este número que decide o saldo mínimo para começar a conversar — não o custo real do prompt.') }}
    </p>

    <div class="form-fields">
      <input-number
        v-model="outputTokens"
        elem-id="reserve-output-tokens"
        :label="t('Tokens de saída assumidos (pior caso)')"
        data-testid="reserve-output-tokens-input"
      />
      <input-number
        v-model="inputMissTokens"
        elem-id="reserve-input-miss-tokens"
        :label="t('Tokens de entrada sem cache (pior caso)')"
        data-testid="reserve-input-miss-tokens-input"
      />
    </div>

    <p class="reserve-hint" data-testid="reserve-settings-hint">
      {{ floorHint }}
    </p>

    <div v-if="errorMessage" class="error-message" data-testid="reserve-settings-error">
      {{ errorMessage }}
    </div>

    <div class="form-actions">
      <Button
        :label="t('Salvar reserva')"
        icon="pi pi-save"
        severity="primary"
        :loading="savingReserveSettings"
        data-testid="reserve-settings-save-button"
        @click="handleSave"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import Button from '../../../ui/AppButton.vue'
import { eventBus } from '../../../eventBus'
import { log } from '../../../utils/DebugLogger'
import { useAiSettings } from '../../../composables/useAiSettings'
import { emitValidationFailed } from '../../../composables/useFormValidation'

/**
 * The admission hold's worst-case assumptions.
 *
 * The reserve prices a worst-case turn BEFORE the stream opens, so these two
 * numbers — not the real cost of a prompt — set the minimum balance a franchise
 * needs to start talking. They were constants in two controllers until
 * 2026-08-21, when the per-franchise peg made the floor scale per franchise: at
 * a 10x peg, a franchise needed ~358 credits to begin a turn that actually cost
 * about 10.
 *
 * Lowering them lowers everyone's entry floor AND the safety margin on the final
 * turn — settle is allowed to exceed the hold, and the platform absorbs the
 * difference. The hint below states the consequence in credits so that trade is
 * visible while it is being made.
 */
const { t } = useI18n()
const { reserveSettings, loadReserveSettings, saveReserveSettings, savingReserveSettings } =
  useAiSettings()

/** Mirrors AiCredits_ReserveSettingsInput::MIN_TOKENS / MAX_TOKENS. */
const MIN_TOKENS = 1
const MAX_TOKENS = 1_000_000

const loaded = ref(false)
const outputTokens = ref<number | null>(null)
const inputMissTokens = ref<number | null>(null)
const errorMessage = ref<string>('')

const floorHint = computed<string>(() => {
  const micro = reserveSettings.value?.reserve_floor_micro ?? 0
  if (!micro) return t('Sem preço de referência para calcular o piso.')
  const credits = micro / 1_000_000
  // Split, never interpolated into the t() key — an untranslated key is
  // returned verbatim and never interpolated.
  return `${t('No peg global, uma franquia precisa de ~')}${credits.toFixed(2)}${t(' créditos disponíveis para iniciar uma conversa.')}`
})

function validate(): string {
  const values: Array<[string, number | null]> = [
    [t('Tokens de saída'), outputTokens.value],
    [t('Tokens de entrada sem cache'), inputMissTokens.value],
  ]
  for (const [label, value] of values) {
    if (value === null || !Number.isInteger(value)) {
      return `${label}: ${t('informe um número inteiro')}`
    }
    if (value < MIN_TOKENS || value > MAX_TOKENS) {
      return `${label}: ${t('fora do intervalo permitido')}`
    }
  }
  return ''
}

async function handleSave(): Promise<void> {
  const problem = validate()
  errorMessage.value = problem
  if (problem !== '') {
    emitValidationFailed('ReserveSettingsForm', { output_tokens: problem })
    eventBus.emit('globalMessage.error', problem)
    return
  }

  try {
    await saveReserveSettings({
      output_tokens: Number(outputTokens.value),
      input_miss_tokens: Number(inputMissTokens.value),
    })
    await loadReserveSettings()
    syncFromLoaded()
    eventBus.emit('globalMessage.success', t('Reserva de admissão salva'))
  } catch (err) {
    log('ReserveSettingsForm', 'handleSave', 'Erro ao salvar reserva', err)
    eventBus.emit('globalMessage.error', t('Erro ao salvar a reserva de admissão'))
  }
}

function syncFromLoaded(): void {
  outputTokens.value = reserveSettings.value?.output_tokens ?? null
  inputMissTokens.value = reserveSettings.value?.input_miss_tokens ?? null
}

onMounted(async () => {
  try {
    await loadReserveSettings()
    syncFromLoaded()
  } catch (err) {
    log('ReserveSettingsForm', 'onMounted', 'Erro ao carregar reserva', err)
  } finally {
    loaded.value = true
  }
})
</script>

<style scoped>
.settings-section {
  background: var(--p-surface-0, #fff);
  border: 1px solid var(--p-surface-200, #e5e7eb);
  border-radius: 8px;
  padding: 20px;
  margin-top: 20px;
}

.section-title {
  margin: 0 0 4px;
}

.section-subtitle {
  margin: 0 0 12px;
  font-size: 0.85rem;
  color: var(--p-text-muted-color, #6b7280);
}

.reserve-hint {
  margin: 8px 0 0;
  font-size: 0.85rem;
  color: var(--p-text-muted-color, #6b7280);
}

.error-message {
  color: var(--p-red-500, #ef4444);
  font-size: 0.9rem;
  margin-top: 8px;
}

.form-actions {
  margin-top: 12px;
}
</style>
